import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Shield, AlertTriangle, Trash2, MessageCircle, Bell, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, isPushEnabled, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const [alias, setAlias] = useState(user?.alias || "");
  const [phone, setPhone] = useState(user?.phone_e164 || "");
  const [optIn, setOptIn] = useState(!!user?.whatsapp_opt_in);
  const [busy, setBusy] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user && ["admin", "superadmin", "editor", "moderator"].includes(user.role);
  const canPush = pushSupported();

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Notificaciones desactivadas.");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("¡Notificaciones activadas! Te avisaremos de cada nueva encuesta Relámpago.");
      }
    } catch (e) {
      const m = e?.message === "denied"
        ? "Permiso denegado. Actívalo en los ajustes del navegador."
        : e?.message === "not-configured"
        ? "Las notificaciones no están disponibles ahora mismo."
        : "No se pudieron activar las notificaciones en este dispositivo.";
      toast.error(m);
    } finally { setPushBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/auth/profile", { alias, phone_e164: phone, whatsapp_opt_in: optIn });
      setUser(r.data);
      toast.success("Perfil actualizado.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar."); } finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (newPw.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw || undefined, new_password: newPw });
      setCurPw(""); setNewPw("");
      toast.success("Contraseña actualizada.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar la contraseña."); } finally { setPwBusy(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const r = await api.delete("/auth/account");
      toast.success(r.data?.message || "Cuenta eliminada.");
      setUser(null);
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la cuenta.");
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="profile-page">
      <div className="flex items-center gap-4 mb-8">
        {user.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full" /> :
          <div className="w-16 h-16 rounded-full bg-slate-200" />}
        <div>
          <h1 className="font-heading font-black text-2xl text-slate-900">{user.alias || user.name}</h1>
          <p className="text-slate-500 text-sm">{user.email}</p>
          <span className="inline-block mt-1 text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-semibold capitalize">{user.role}</span>
        </div>
      </div>

      {isAdmin && (
        <Link to="/admin" data-testid="profile-admin-link"
          className="flex items-center gap-3 bg-slate-900 text-white rounded-lg p-4 mb-6 hover:bg-slate-800 transition-colors">
          <Shield className="w-5 h-5 text-cyan-400" />
          <div>
            <p className="font-semibold">Panel CMS</p>
            <p className="text-sm text-slate-400">Gestiona contenido, consultas y reportes</p>
          </div>
        </Link>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Preferencias de la cuenta</h3>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nombre público o alias</label>
        <input value={alias} onChange={(e) => setAlias(e.target.value)} data-testid="profile-alias"
          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500 mb-2" placeholder="Cómo quieres aparecer públicamente" />
        <p className="text-xs text-slate-400 mb-4">Tu cuenta se mantiene verificada internamente aunque uses un alias.</p>
        <button onClick={save} disabled={busy} data-testid="profile-save"
          className="bg-cyan-500 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-cyan-600 transition-colors disabled:opacity-60">
          {busy ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      {/* WhatsApp opt-in */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mt-6" data-testid="whatsapp-optin">
        <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900 mb-2">
          <MessageCircle className="w-5 h-5 text-emerald-500" /> Avisos por WhatsApp
        </h3>
        <p className="text-sm text-slate-600 mb-4">Recibe un aviso cuando publiquemos una nueva encuesta relámpago. Puedes darte de baja cuando quieras.</p>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Número de WhatsApp (formato internacional)</label>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone"
          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500 mb-3" placeholder="+17875551234" />
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} data-testid="profile-whatsapp-optin"
            className="w-4 h-4 accent-emerald-500 mt-0.5" />
          <span className="text-sm text-slate-700">Acepto recibir encuestas y avisos de 2Voto por WhatsApp. Entiendo que puedo desactivarlo aquí o respondiendo <strong>STOP</strong>.</span>
        </label>
        <button onClick={save} disabled={busy} data-testid="whatsapp-save"
          className="mt-4 bg-emerald-500 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-emerald-600 transition-colors disabled:opacity-60">
          {busy ? "Guardando…" : "Guardar preferencia"}
        </button>
      </div>

      {/* Push notifications */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mt-6" data-testid="push-optin">
        <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900 mb-2">
          <Bell className="w-5 h-5 text-cyan-500" /> Notificaciones push
        </h3>
        <p className="text-sm text-slate-600 mb-4">Recibe un aviso en este dispositivo cuando abra una nueva encuesta Relámpago, aunque no tengas 2Voto abierto.</p>
        {canPush ? (
          <button onClick={togglePush} disabled={pushBusy} data-testid="push-toggle"
            className={`inline-flex items-center gap-2 font-semibold px-6 py-2.5 rounded-full transition-colors disabled:opacity-60 ${pushOn ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-cyan-500 text-white hover:bg-cyan-600"}`}>
            <Bell className="w-4 h-4" /> {pushBusy ? "Procesando…" : pushOn ? "Desactivar notificaciones" : "Activar notificaciones"}
          </button>
        ) : (
          <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">Este navegador no admite notificaciones push. En iPhone, primero instala 2Voto en tu pantalla de inicio (iOS 16.4+).</p>
        )}
      </div>

      {isAdmin && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 mt-6" data-testid="admin-password-card">
          <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900 mb-2">
            <KeyRound className="w-5 h-5 text-slate-700" /> Contraseña de administrador
          </h3>
          <p className="text-sm text-slate-600 mb-4">Establece o cambia tu contraseña para entrar al panel con tu correo (sin Google).</p>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Contraseña actual <span className="font-normal text-slate-400">(si ya tienes una)</span></label>
          <input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password"
            className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500 mb-3" placeholder="••••••••" />
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nueva contraseña</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password"
            className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-cyan-500 mb-3" placeholder="Mínimo 8 caracteres" />
          <button onClick={changePassword} disabled={pwBusy} data-testid="change-password-btn"
            className="bg-slate-900 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-slate-800 transition-colors disabled:opacity-60">
            {pwBusy ? "Guardando…" : "Guardar contraseña"}
          </button>
        </div>
      )}

      {/* Danger zone */}
      <div className="bg-white rounded-lg border border-rose-200 p-6 mt-6" data-testid="danger-zone">
        <h3 className="flex items-center gap-2 font-heading font-bold text-lg text-rose-600 mb-2">
          <AlertTriangle className="w-5 h-5" /> Eliminar mi cuenta
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Esta acción es permanente. Se eliminarán tu cuenta, tus votos, tus firmas y los reportes que enviaste.
          Las peticiones o preguntas públicas que hayas creado se conservarán de forma anónima porque otras personas pudieron participar en ellas.
        </p>
        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)} data-testid="delete-account-button"
            className="inline-flex items-center gap-2 border border-rose-300 text-rose-600 font-semibold px-5 py-2.5 rounded-full hover:bg-rose-50 transition-colors">
            <Trash2 className="w-4 h-4" /> Eliminar mi cuenta
          </button>
        ) : (
          <div className="space-y-3" data-testid="delete-confirm">
            <p className="text-sm text-slate-700">Para confirmar, escribe <strong>ELIMINAR</strong> a continuación:</p>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-input"
              placeholder="ELIMINAR" className="w-full max-w-xs border border-slate-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-500" />
            <div className="flex gap-3">
              <button onClick={deleteAccount} disabled={confirmText !== "ELIMINAR" || deleting} data-testid="delete-confirm-button"
                className="inline-flex items-center gap-2 bg-rose-500 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-40">
                <Trash2 className="w-4 h-4" /> {deleting ? "Eliminando…" : "Eliminar definitivamente"}
              </button>
              <button onClick={() => { setConfirmDelete(false); setConfirmText(""); }} className="text-slate-500 font-medium px-4">Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
